import axios from "axios";
import { getAuth } from "../../../modules/auth/core/AuthHelpers";
import { encryptId } from "../../../modules/auth/core/crypto";
import type { RoleList, Users, UsersPayload } from "../types/users.type";

const API_URL = import.meta.env.VITE_APP_API_URL;

export const fetchUsers = async (
	role_id?: string,
	page = 1,
	limit = 10,
	search = "",
): Promise<any> => {
	try {
		const params = new URLSearchParams({
			page: String(page),
			limit: String(limit),
		});
		if (search) params.append("search", search);
		if (role_id) params.append("role_id", role_id);

		const response = await axios.get(
			`${API_URL}/admin/user/list?${params.toString()}`,
		);
		return response.data.data;
	} catch (error) {
		throw new Error(
			axios.isAxiosError(error)
				? error.response?.data?.message
				: "Failed to fetch users",
		);
	}
};

export const createUsers = async (payload: UsersPayload) => {
	return axios.post(`${API_URL}/admin/user/add`, payload);
};
export const refreshToken = async () => {
	const auth = getAuth();
	if (!auth) {
		throw new Error("No auth data found");
	}
	const token = auth.access_token || auth.token;
	if (!token) {
		throw new Error("No access token found");
	}
	const config = {
		withCredentials: true,
		headers: { Authorization: `${token}` },
	};
	return axios.post(`${API_URL}/auth/refresh-token`, {}, config);
};
export const changePassword = async (payload: any) => {
	return axios.post(`${API_URL}/admin/user/change-password`, payload);
};

export const updateUsers = async (id: number, payload: UsersPayload) => {
	return axios.put(`${API_URL}/admin/user/edit/${id}`, payload);
};

export const deleteUser = async (id: number) => {
	return axios.delete(`${API_URL}/admin/user/delete/${id}`);
};
export const usersGetById = async (
	id: number | undefined,
): Promise<UsersPayload> => {
	try {
		const response = await axios.get(`${API_URL}/admin/user/view/${id}`);
		const data = response.data.data;
		return Array.isArray(data) ? data[0] : data;
	} catch (error) {
		throw new Error(
			axios.isAxiosError(error)
				? error.response?.data?.message
				: "Failed to fetch user",
		);
	}
};

export const fetchGeneratedEmployeeCode = async (
	roleId: number | string,
): Promise<string | null> => {
	const response = await axios.get(
		`${API_URL}/admin/user/generate-employee-code`,
		{ params: { role_id: roleId } },
	);
	return response.data?.data?.employee_code ?? null;
};

export const fetchRoleList = async (): Promise<RoleList[]> => {
	const response = await axios.get(`${API_URL}/admin/master/role-list`);
	const data = response.data.data;
	return Array.isArray(data) ? data : data?.items || [];
};

export const getUserIDCard = async (): Promise<any> => {
	const auth = getAuth();
	const token = auth?.access_token || auth?.token || "";

	// Create a new instance to bypass global interceptors that might trigger logout
	const cleanAxios = axios.create();

	try {
		const response = await cleanAxios.get(
			`${API_URL}/admin/users/user-id-card`,
			{
				headers: { Authorization: `${token}` },
			},
		);
		return response.data;
	} catch (error) {
		console.error("Custom ID Card API failed", error);
		// Return null or empty object if it fails, ensuring we don't crash or logout
		return null;
	}
};
